/**
 * Core StatCard Component
 *
 * KPI/metric display card with title, value, trend indicator, sparkline slot,
 * icon, and click-through. Used in dashboards and overview pages.
 *
 * @example
 *   <StatCard title="Total Users" value="12,847" trend={{ value: 12.5, direction: 'up' }} icon={<Users />} />
 *   <StatCard title="Error Rate" value="0.3%" trend={{ value: -15, direction: 'down' }} variant="danger" />
 */
import React from 'react';
import { cn } from '@/lib/utils';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';

export interface StatTrend {
  /** Percentage change */
  value: number;
  /** Direction: up = positive, down = negative */
  direction: 'up' | 'down' | 'neutral';
  /** Label after the number, e.g. "vs last month" */
  label?: string;
}

export interface StatCardProps {
  /** Metric title */
  title: string;
  /** Primary value display */
  value: React.ReactNode;
  /** Trend indicator */
  trend?: StatTrend;
  /** Icon in the top-right corner */
  icon?: React.ReactNode;
  /** Color variant */
  variant?: 'default' | 'success' | 'warning' | 'danger' | 'info';
  /** Subtitle or description */
  subtitle?: string;
  /** Sparkline or chart slot */
  chart?: React.ReactNode;
  /** Click handler */
  onClick?: () => void;
  /** Loading state */
  loading?: boolean;
  className?: string;
}

const iconBg: Record<string, string> = {
  default: 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400',
  success: 'bg-emerald-50 text-emerald-500 dark:bg-emerald-900/20 dark:text-emerald-400',
  warning: 'bg-amber-50 text-amber-500 dark:bg-amber-900/20 dark:text-amber-400',
  danger: 'bg-red-50 text-red-500 dark:bg-red-900/20 dark:text-red-400',
  info: 'bg-blue-50 text-blue-500 dark:bg-blue-900/20 dark:text-blue-400',
};

const trendColors = {
  up: 'text-emerald-600 dark:text-emerald-400',
  down: 'text-red-600 dark:text-red-400',
  neutral: 'text-slate-400',
};

const TrendIcon = ({ direction }: { direction: 'up' | 'down' | 'neutral' }) => {
  if (direction === 'up') return <TrendingUp className="h-3.5 w-3.5" />;
  if (direction === 'down') return <TrendingDown className="h-3.5 w-3.5" />;
  return <Minus className="h-3.5 w-3.5" />;
};

export const StatCard: React.FC<StatCardProps> = ({
  title,
  value,
  trend,
  icon,
  variant = 'default',
  subtitle,
  chart,
  onClick,
  loading = false,
  className = '',
}) => (
  <div
    onClick={onClick}
    className={cn(
      'bg-white dark:bg-slate-900 rounded-2xl border border-gray-100 dark:border-slate-800 p-5',
      'transition-all duration-200',
      onClick && 'cursor-pointer hover:shadow-lg hover:-translate-y-0.5',
      className,
    )}
  >
    <div className="flex items-start justify-between mb-3">
      <span className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">
        {title}
      </span>
      {icon && (
        <div className={cn('p-2 rounded-xl', iconBg[variant])}>
          {icon}
        </div>
      )}
    </div>

    {loading ? (
      <div className="space-y-2">
        <div className="h-8 w-24 rounded bg-slate-200 dark:bg-slate-700 animate-pulse" />
        <div className="h-3 w-16 rounded bg-slate-100 dark:bg-slate-800 animate-pulse" />
      </div>
    ) : (
      <>
        <div className="text-2xl font-bold text-slate-900 dark:text-white mb-1">
          {value}
        </div>

        <div className="flex items-center gap-2">
          {trend && (
            <span className={cn('inline-flex items-center gap-0.5 text-xs font-semibold', trendColors[trend.direction])}>
              <TrendIcon direction={trend.direction} />
              {Math.abs(trend.value)}%
            </span>
          )}
          {(trend?.label || subtitle) && (
            <span className="text-[11px] text-slate-400">
              {trend?.label || subtitle}
            </span>
          )}
        </div>
      </>
    )}

    {chart && <div className="mt-3 -mx-1">{chart}</div>}
  </div>
);
export default StatCard;
